// 실패한 CSS를 다시 요청하지 않고 브라우저가 이미 가진 진단 단서만 읽는다.
const TIMING_FIELDS = [
  'startTime', 'duration', 'fetchStart', 'domainLookupStart', 'domainLookupEnd',
  'connectStart', 'connectEnd', 'secureConnectionStart', 'requestStart',
  'responseStart', 'responseEnd', 'workerStart', 'transferSize', 'encodedBodySize', 'decodedBodySize'
]
const finite = value => typeof value === 'number' && Number.isFinite(value) && value >= 0
  ? Math.round(value * 1000) / 1000 : null
const choice = (value, values) => values.includes(value) ? value : 'unknown'

function cssUrl(value) {
  try {
    const url = new URL(value, window.location.href)
    if (url.origin !== window.location.origin || url.username || url.password
        || !/^\/assets\/[A-Za-z0-9_./-]+\.css$/.test(url.pathname) || url.pathname.length > 255) return null
    url.hash = ''
    return url
  } catch (_) { return null }
}

function browserVersion() {
  const ua = navigator.userAgent || ''
  for (const [name, pattern] of [
    ['Edge', /Edg(?:A|iOS)?\/(\d+)/], ['Chrome', /(?:Chrome|CriOS)\/(\d+)/],
    ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/], ['Safari', /Version\/(\d+).*Safari/]
  ]) {
    const match = ua.match(pattern)
    if (match) return `${name}/${match[1]}`
  }
  return 'unknown'
}

export function installCssFailureDiagnostics(emit) {
  const recent = new Map()
  const resources = []
  const policies = []
  let observer
  let disposed = false
  // 브라우저 버퍼를 비우거나 늘리지 않고 CSS 항목만 최대 64개 유지한다.
  try {
    observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        if (cssUrl(entry.name) && entry.initiatorType === 'link') resources.push(entry)
      }
      resources.splice(0, Math.max(0, resources.length - 64))
    })
    observer.observe({ type: 'resource', buffered: true })
  } catch (_) { observer?.disconnect(); observer = undefined }

  const timing = item => {
    if (!observer && typeof performance.getEntriesByName !== 'function') return { match: 'unsupported' }
    let entries = resources.filter(entry => entry.name === item.url)
    try { entries = entries.concat(performance.getEntriesByName(item.url, 'resource')) } catch (_) {}
    const candidates = new Map()
    for (const entry of entries) {
      // 같은 URL의 과거 성공 기록을 연결하지 않는다. 근접 항목도 확정 요청이 아닌 후보다.
      if (entry.initiatorType !== 'link' || entry.responseEnd <= 0
          || entry.responseEnd < item.at - 2000 || entry.responseEnd > item.at + 100
          || entry.startTime > item.at) continue
      candidates.set(`${entry.startTime}:${entry.responseEnd}:${entry.duration}`, entry)
    }
    if (candidates.size !== 1) return { match: candidates.size ? 'ambiguous' : 'not_found' }
    const entry = [...candidates.values()][0]
    const result = { match: 'candidate' }
    for (const field of TIMING_FIELDS) result[field] = finite(entry[field])
    result.responseStatus = Number.isInteger(entry.responseStatus) && entry.responseStatus >= 100
      && entry.responseStatus <= 599 ? entry.responseStatus : null
    result.nextHopProtocol = choice(entry.nextHopProtocol, ['h2', 'h3', 'http/1.1', 'http/1.0'])
    result.deliveryType = choice(entry.deliveryType, ['cache', 'navigational-prefetch'])
    result.contentType = choice(entry.contentType,
      ['text/css', 'text/html', 'text/plain', 'application/xhtml+xml', 'application/octet-stream'])
    result.contentEncoding = choice(entry.contentEncoding, ['gzip', 'br', 'deflate', 'identity', 'zstd'])
    return result
  }

  const onPolicy = event => {
    try {
      const url = cssUrl(event.blockedURI)
      if (!url || !['style-src', 'style-src-elem', 'default-src'].includes(event.effectiveDirective)
          || !['enforce', 'report'].includes(event.disposition)) return
      policies.push({ url: url.href, at: performance.now(), directive: event.effectiveDirective,
        disposition: event.disposition })
      if (policies.length > 16) policies.shift()
    } catch (_) {}
  }

  const enqueue = (url, signal, info = '') => {
    const now = Date.now()
    const route = window.location.pathname.slice(0, 255)
    const key = `${route}\n${url.pathname}`
    for (const [oldKey, item] of recent) {
      if (!item.timer && now - item.wall > 10000) recent.delete(oldKey)
    }
    const existing = recent.get(key)
    if (existing) {
      existing.diagnostics[signal] = true
      if (info.startsWith('module_load ')) existing.info = info
      return
    }
    if (recent.size >= 20) return
    let navigationType = 'unknown'
    try { navigationType = choice(performance.getEntriesByType('navigation')[0]?.type,
      ['navigate', 'reload', 'back_forward', 'prerender']) } catch (_) {}
    const at = performance.now()
    const item = { url: url.href, at, wall: now, route, info, diagnostics: {
      version: 1, assetPath: url.pathname, observedAt: new Date(now).toISOString(),
      timezoneOffsetMinutes: new Date(now).getTimezoneOffset(),
      timeOrigin: finite(performance.timeOrigin), observedAtPerformanceMs: finite(at),
      online: typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
      visibility: choice(document.visibilityState, ['visible', 'hidden', 'prerender']),
      serviceWorkerControlled: navigator.serviceWorker ? !!navigator.serviceWorker.controller : null,
      navigationType, browser: browserVersion(), linkError: false, preloadError: false
    } }
    item.diagnostics[signal] = true
    recent.set(key, item)
    // 같은 실패의 link error, Vite 예외, CSP 이벤트를 합치고 화면 처리는 그대로 진행한다.
    item.timer = setTimeout(() => {
      item.timer = null
      if (disposed) return
      try {
        item.diagnostics.timing = timing(item)
        item.diagnostics.csp = policies.filter(policy => policy.url === item.url
          && Math.abs(policy.at - item.at) <= 2000).slice(-4)
          .map(({ directive, disposition }) => ({ directive, disposition }))
        emit({ error: { message: `Unable to preload CSS for ${url.pathname}` },
          info: item.info, route: item.route, diagnostics: item.diagnostics })
      } catch (_) { /* 진단 실패가 화면 오류를 만들지 않는다. */ }
    }, 100)
  }

  const onResourceError = event => {
    try {
      const link = event.target
      if (link?.tagName !== 'LINK' || !(link.relList.contains('stylesheet')
          || (link.relList.contains('preload') && link.as === 'style'))) return
      const url = cssUrl(link.href)
      if (url) enqueue(url, 'linkError')
    } catch (_) {}
  }
  window.addEventListener('error', onResourceError, true)
  document.addEventListener('securitypolicyviolation', onPolicy)
  return {
    capture(error, info = '') {
      try {
        const message = typeof error === 'string' ? error : error?.message
        const match = typeof message === 'string' && message.match(/^Unable to preload CSS for (\S+)$/)
        const url = match && cssUrl(match[1])
        if (!url) return false
        enqueue(url, 'preloadError', info)
        return true
      } catch (_) { return false }
    },
    dispose() {
      disposed = true
      window.removeEventListener('error', onResourceError, true)
      document.removeEventListener('securitypolicyviolation', onPolicy)
      observer?.disconnect()
      recent.forEach(item => clearTimeout(item.timer))
      recent.clear()
      resources.length = policies.length = 0
    }
  }
}
